import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAdminApi, selectCls, btnPrimary } from "./useAdminApi";
import { errMsg } from "@/lib/api";

const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const EMPTY = { patient_name: "", patient_phone: "", notes: "", amount_paid: "" };

export const WalkInDialog = ({ open, onOpenChange, onCreated }) => {
  const call = useAdminApi();
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState("");
  const [date, setDate] = useState(todayIST());
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState("");
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    call("get", "/admin/visit-reasons").then((r) => { setReasons(r); setReasonId((v) => v || r.find((x) => x.active)?.id || ""); });
  }, [open, call]);
  useEffect(() => {
    if (!open || !date) return;
    setDay(null); setSlot("");
    call("get", "/admin/slots", null, { date }).then(setDay).catch((e) => toast.error(errMsg(e)));
  }, [open, date, call]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await call("post", "/admin/appointments/walkin", {
        visit_reason_id: reasonId, appointment_date: date, slot_time: slot, patient_name: f.patient_name,
        patient_phone: f.patient_phone, notes: f.notes, amount_paid: f.amount_paid === "" ? null : Number(f.amount_paid),
      });
      toast.success(`Booked ${r.booking_ref} · ${r.slot_label}`);
      setF(EMPTY); setSlot(""); onOpenChange(false); onCreated();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const fee = reasons.find((r) => r.id === reasonId)?.fee;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="walkin-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Walk-in / Phone booking</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-xl" data-testid="walkin-date" />
            <select value={reasonId} onChange={(e) => setReasonId(e.target.value)} className={selectCls} data-testid="walkin-reason">
              {reasons.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
          <div className="min-h-[60px]">
            {!day ? <Loader2 className="h-4 w-4 animate-spin" /> : day.closed ? <p className="text-sm text-red-600" data-testid="walkin-closed">Closed: {day.reason}. Open this date in Schedule first.</p> : (
              <div className="grid grid-cols-4 gap-1.5" data-testid="walkin-slots">
                {day.slots.map((s) => {
                  const taken = s.status === "booked" || s.status === "held";
                  return (
                    <button key={s.time} type="button" disabled={taken} onClick={() => setSlot(s.time)} data-testid={`walkin-slot-${s.time}`}
                      className={`h-9 rounded-lg border text-xs ${slot === s.time ? "bg-teal text-white border-teal" : taken ? "bg-slate-50 text-slate-300 line-through" : s.status === "available" ? "bg-white hover:bg-mint" : "bg-amber-50 border-amber-200 text-amber-700"}`}
                      title={s.status}>{s.label}</button>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-ink-muted mt-1">Amber = blocked or past (staff can still book). Struck-through = taken.</p>
          </div>
          <Input placeholder="Patient name" value={f.patient_name} onChange={(e) => setF({ ...f, patient_name: e.target.value })} className="h-10 rounded-xl" data-testid="walkin-name" />
          <Input placeholder="10-digit mobile" inputMode="numeric" value={f.patient_phone} onChange={(e) => setF({ ...f, patient_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="h-10 rounded-xl" data-testid="walkin-phone" />
          <Input placeholder="Note (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="h-10 rounded-xl" data-testid="walkin-notes" />
          <Input type="number" min="0" placeholder={`Fee collected (default ₹${fee ?? ""})`} value={f.amount_paid} onChange={(e) => setF({ ...f, amount_paid: e.target.value })} className="h-10 rounded-xl" data-testid="walkin-amount" />
          <button type="submit" disabled={busy || !slot || !reasonId} className={`${btnPrimary} w-full h-11`} data-testid="walkin-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Book slot
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
